import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  productId: "",
  skU: "",
  barcode: "",
  code: "",
  price: 0,
  salePrice: 0,
  balanceQuantity: 0,
  weight: 0,
  length: 0,
  width: 0,
  height: 0,
  isDefault: false,
  isActive: true,
  createdDate: "",
  updatedDate: ""
});

const toInputDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function ProductVarientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const pRes = await apiGet("/api/Products");
    if (pRes.ok) setProducts(pRes.data);
    else setError(pRes.error ?? "Failed to load products");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductVarients/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product varient");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productId: x.productId ?? "",
        skU: x.skU ?? "",
        barcode: x.barcode ?? "",
        code: x.code ?? "",
        price: x.price ?? 0,
        salePrice: x.salePrice ?? 0,
        balanceQuantity: x.balanceQuantity ?? 0,
        weight: x.weight ?? 0,
        length: x.length ?? 0,
        width: x.width ?? 0,
        height: x.height ?? 0,
        isDefault: Boolean(x.isDefault),
        isActive: Boolean(x.isActive),
        createdDate: toInputDateTime(x.createdDate),
        updatedDate: toInputDateTime(x.updatedDate)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.productId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductVarients", {
      id: editingId ?? 0,
      productId: Number(form.productId),
      skU: form.skU || null,
      barcode: form.barcode || null,
      code: form.code || null,
      price: Number(form.price || 0),
      salePrice: Number(form.salePrice || 0),
      balanceQuantity: Number(form.balanceQuantity || 0),
      weight: Number(form.weight || 0),
      length: Number(form.length || 0),
      width: Number(form.width || 0),
      height: Number(form.height || 0),
      isDefault: Boolean(form.isDefault),
      isActive: Boolean(form.isActive),
      createdDate: form.createdDate || new Date().toISOString(),
      updatedDate: form.updatedDate || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save product varient");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductVarients/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product varient");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productId: x.productId ?? "",
        skU: x.skU ?? "",
        barcode: x.barcode ?? "",
        code: x.code ?? "",
        price: x.price ?? 0,
        salePrice: x.salePrice ?? 0,
        balanceQuantity: x.balanceQuantity ?? 0,
        weight: x.weight ?? 0,
        length: x.length ?? 0,
        width: x.width ?? 0,
        height: x.height ?? 0,
        isDefault: Boolean(x.isDefault),
        isActive: Boolean(x.isActive),
        createdDate: toInputDateTime(x.createdDate),
        updatedDate: toInputDateTime(x.updatedDate)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product Varient" : "Add Product Varient"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-varients-form"
            disabled={saving}
            title="Save"
            aria-label="Save product varient"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-varients-list" className="icon-button icon-button--toolbar" title="Product varients list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-varients-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Product</span>
          <select value={form.productId} onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `Product #${p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>SKU</span>
          <input value={form.skU} onChange={(e) => setForm((p) => ({ ...p, skU: e.target.value }))} />
        </label>
        <label className="field">
          <span>Barcode</span>
          <input value={form.barcode} onChange={(e) => setForm((p) => ({ ...p, barcode: e.target.value }))} />
        </label>
        <label className="field">
          <span>Code</span>
          <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
        </label>
        <label className="field">
          <span>Price</span>
          <input type="number" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} />
        </label>
        <label className="field">
          <span>Sale Price</span>
          <input type="number" value={form.salePrice} onChange={(e) => setForm((p) => ({ ...p, salePrice: e.target.value }))} />
        </label>
        <label className="field">
          <span>Balance Quantity</span>
          <input type="number" value={form.balanceQuantity} onChange={(e) => setForm((p) => ({ ...p, balanceQuantity: e.target.value }))} />
        </label>
        <label className="field">
          <span>Weight</span>
          <input type="number" value={form.weight} onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))} />
        </label>
        <label className="field">
          <span>Length</span>
          <input type="number" value={form.length} onChange={(e) => setForm((p) => ({ ...p, length: e.target.value }))} />
        </label>
        <label className="field">
          <span>Width</span>
          <input type="number" value={form.width} onChange={(e) => setForm((p) => ({ ...p, width: e.target.value }))} />
        </label>
        <label className="field">
          <span>Height</span>
          <input type="number" value={form.height} onChange={(e) => setForm((p) => ({ ...p, height: e.target.value }))} />
        </label>
        <label className="field">
          <span>Created Date</span>
          <input type="datetime-local" value={form.createdDate} onChange={(e) => setForm((p) => ({ ...p, createdDate: e.target.value }))} />
        </label>
        <label className="field">
          <span>Updated Date</span>
          <input type="datetime-local" value={form.updatedDate} onChange={(e) => setForm((p) => ({ ...p, updatedDate: e.target.value }))} />
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm((p) => ({ ...p, isDefault: e.target.checked }))} />
          <span>Is Default</span>
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
          <span>Is Active</span>
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
